import React, { useContext, useEffect, useState } from 'react';
import _ from 'lodash';
import axios from '../../../axios/axios-base';
import { ProcessingAction, ActionMockDto } from '../../../types/rest/assetCatalogTypes';
import { transformConditionsToBase64 } from '../../../utils/transactionUtils';
import { SandBoxContext } from '../SandboxModal';
import acApi from '../../../axios/ac/acApi';
import { useDispatch } from 'react-redux';
import { showToastNotification } from '../../../store/ui/toasts/toastsActions';
import { ToastType } from '../../../store/ui/toasts/toastsTypes';
import BlazeTable from '../../../components/blazeTable/BlazeTable';
import { BlazeTableColDef } from '../../../components/blazeTable/BlazeTable.types';
import ExpandLink from '../../../components/blazeTable/ExpandLink';
import { getProcessingActionType } from '../../../views/AssetCatalog/Transactions/TransactionEditor/ProcessingActions/CommonActions';
import SandboxHttpActionModal from './SandboxHttpActionModal';

interface SandboxProcessingActionsProps {
  transactionId: number;
}

const SandboxProcessingActions = ({ transactionId }: SandboxProcessingActionsProps) => {
  const { state, setState } = useContext(SandBoxContext);
  const dispatch = useDispatch();

  const [actions, setActions] = useState<ProcessingAction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedAction, setSelectedAction] = useState<ProcessingAction | undefined>(undefined);

  const actionMocks: ActionMockDto[] = state.sandboxTransaction?.actionMocks ?? [];

  useEffect(() => {
    fetchActions();
  }, []);

  const fetchActions = async () => {
    try {
      const response = await axios.get(`workspaces/{workspaceId}/transactions/${transactionId}/actions?sort=-id`);
      let processingActions: ProcessingAction[] = _.orderBy(response.data.result, ['priority', 'id'], 'asc');

      // Decode conditions from base64
      processingActions = processingActions.map(pa => ({
        ...pa,
        conditions: transformConditionsToBase64(pa.conditions, 'base64decode'),
      }));

      // Show only WEBHOOK and HTTP_CALL — STATE_UPDATE hidden in sandbox
      const filtered = processingActions.filter(pa => pa.actionType === 'WEBHOOK' || pa.actionType === 'HTTP_CALL');
      setActions(filtered);
    } catch {
      dispatch(showToastNotification(ToastType.Danger, 'Error', 'Failed to load processing actions.'));
    } finally {
      setIsLoading(false);
    }
  };

  const getMockForAction = (action: ProcessingAction): ActionMockDto | undefined => {
    return actionMocks.find(m => m.actionId === action.id);
  };

  const handleSaveMock = async (mock: ActionMockDto) => {
    const updatedMocks = [...actionMocks.filter(m => m.actionId !== mock.actionId), mock];

    try {
      await acApi.sandbox.updateActionMocks(updatedMocks);
      setState(prevState => ({
        ...prevState,
        sandboxTransaction: {
          ...prevState.sandboxTransaction,
          actionMocks: updatedMocks,
        },
      }));
      dispatch(showToastNotification(ToastType.Success, 'Mock Saved', `Mock response saved for "${mock.actionName}".`));
    } catch {
      dispatch(showToastNotification(ToastType.Danger, 'Error', 'Failed to save mock response.'));
    }
    setSelectedAction(undefined);
  };

  const columns: BlazeTableColDef[] = [
    {
      header: 'Name',
      accessor: 'name',
      cellComponent: (action: ProcessingAction) => (
        <ExpandLink
          value={action.name ?? ''}
          id={action.id as number}
          title={action.name}
          onToggleExpandDetail={() => setSelectedAction(action)}
        />
      ),
    },
    {
      header: 'Type',
      accessor: 'actionType',
      cellComponent: (action: ProcessingAction) => getProcessingActionType(action.actionType),
    },
    {
      header: 'Mock Values',
      accessor: 'mock',
      width: 100,
      cellComponent: (action: ProcessingAction) => {
        const hasMock = !!getMockForAction(action);
        return (
          <span className={`badge badge-${hasMock ? 'primary' : 'secondary'}`}>
            {hasMock ? 'Configured' : 'Not Set'}
          </span>
        );
      },
    },
  ];

  return (
    <div className="p-4">
      {selectedAction && (
        <SandboxHttpActionModal
          processingAction={selectedAction}
          existingMock={getMockForAction(selectedAction)}
          onClose={() => setSelectedAction(undefined)}
          onSave={handleSaveMock}
        />
      )}

      {actions.length !== 0 && (
        <div className="mb-4">
          <span>
            Mock the responses of Webhooks and HTTP Calls attached to this transaction. Click an action to configure its
            sandbox response.
          </span>
        </div>
      )}
      {actions.length === 0 && !isLoading ? (
        <p className="text-muted">No Webhook or HTTP Call actions found for this transaction.</p>
      ) : (
        <div style={{ width: '50%' }}>
          <BlazeTable
            data={actions}
            isLoading={isLoading}
            options={{
              showRowExpander: false,
              pageable: false,
              selectable: false,
              columnDef: columns,
            }}
          />
        </div>
      )}
    </div>
  );
};

export default SandboxProcessingActions;
